import {
  AdminInput,
  BrandsIcon,
  Disclaimer,
  Div,
  DropdownMenuComponent,
  ErrorToast,
  H5,
  Icon,
  Img,
  lightTheme,
  Overlay,
  overlayAnimationVariant,
  P,
  Radio,
  restaurantImage,
  useDisableBodyScroll,
  X,
} from '@vgrubs/components';
import { SimpleBrandEntity } from 'components/src/types/brand.types';
import { SimpleLocationEntity } from 'components/src/types/location.types';
import 'flatpickr/dist/flatpickr.css';
import { AnimatePresence } from 'framer-motion';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { LocationsPicker, ScrollableWrapper } from './AdminBrandsPickerStyled';

export const AdminSingleBrandPicker = ({
  selectedLocation,
  selectedBrand,
  setSelectedBrand,
  preselectIfEmpty,
  brands,
  placeholder = 'Select Brand',
}: {
  selectedLocation: SimpleLocationEntity | null;
  selectedBrand: SimpleBrandEntity | null;
  setSelectedBrand:
    | Dispatch<SetStateAction<SimpleBrandEntity | null>>
    | ((brand: SimpleBrandEntity | null) => void);
  preselectIfEmpty?: boolean;
  /** Brands to choose from instead of every brand on the location. */
  brands?: SimpleBrandEntity[];
  /** Shown while no brand is picked. */
  placeholder?: string;
}) => {
  const [dropdown, setDropdown] = useState(false);
  const [search, setSearch] = useState('');

  useDisableBodyScroll(dropdown);

  const data = brands ?? selectedLocation?.brands;
  const handleSelectLocation = (newItem: SimpleBrandEntity) => {
    setSelectedBrand(newItem);
    setDropdown((prev) => !prev);
  };

  useEffect(() => {
    if (selectedLocation && preselectIfEmpty) {
      setSelectedBrand(data?.[0] || null);
    }
  }, [selectedLocation]);

  function isLocationSelected(item: SimpleBrandEntity) {
    return selectedBrand?.id === item.id;
  }

  function renderLocationInputPlaceholder() {
    if (data?.length === 0) {
      return 'No Brands';
    } else if (selectedBrand) {
      return selectedBrand.name;
    } else return placeholder;
  }

  const handleOpen = () => {
    if (!selectedLocation) {
      ErrorToast('Please select a location');
    } else if (data?.length === 0) {
      ErrorToast('This location has no brands');
    } else {
      setDropdown((prev) => !prev);
    }
  };

  const filteredBrands = data?.filter((brand) =>
    brand.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AnimatePresence>
      <Div $relative $height="45px" $heightSM="45px">
        <LocationsPicker
          $opened={dropdown}
          aria-haspopup="dialog"
          aria-expanded={dropdown}
          aria-label="Select brand"
          // Wrapper has no onClick here (an inner Div carries it), so
          // override the default keyboard handler from
          // AdminBrandsPickerStyled to call handleOpen directly.
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOpen();
            }
          }}
        >
          <Div
            onClick={handleOpen}
            $width="100%"
            $height="45px"
            $flex
            $alignCenter
          >
            {renderLocationInputPlaceholder()}
          </Div>

          <Div $flexNone $flex $alignCenter $gap="12px">
            {selectedBrand && (
              <Icon $size onClick={() => setSelectedBrand(null)}>
                <X />
              </Icon>
            )}

            <Icon $opacity="0.5" onClick={handleOpen}>
              <BrandsIcon />
            </Icon>
          </Div>
        </LocationsPicker>

        <DropdownMenuComponent
          isOpen={dropdown}
          maxHeight="70vh"
          padding="0"
          fullWidth
        >
          <Div $p="20px">
            <AdminInput
              search
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              placeholder="Search Brand"
            />
          </Div>
          <ScrollableWrapper>
            {filteredBrands?.length === 0 && (
              <Div
                $flex
                $center
                $column
                $height="calc(70vh - 300px)"
                $heightSM="calc(60vh - 300px)"
              >
                <Img src={restaurantImage} alt="restaurant" $width="250px" />
                <H5>No Brands</H5>
              </Div>
            )}
            {filteredBrands?.map((l, i) => (
              <Div
                key={i}
                $box
                $p="18px 24px"
                $mb="8"
                $flex
                $alignCenter
                $between
                onClick={() => handleSelectLocation(l)}
                $gap="16px"
                $pointer
              >
                <div>
                  <P
                    $bold
                    $mb="4"
                    $color={isLocationSelected(l) && lightTheme.primary}
                  >
                    {l.name}
                  </P>
                  <Disclaimer $mb="0">{''}</Disclaimer>
                </div>

                <Radio checked={isLocationSelected(l)} />
              </Div>
            ))}
          </ScrollableWrapper>
        </DropdownMenuComponent>
      </Div>

      {dropdown && (
        <Overlay
          key="modalOverlay"
          variants={overlayAnimationVariant}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={() => setDropdown(false)}
        />
      )}
    </AnimatePresence>
  );
};
